import {
  isTeammateATeamMember,
  type Teammate,
} from "@repo/app-types/entities/teammate";
import { Navigate, type RouteObject } from "react-router";
import {
  TeamMemberNavigationSlideoverContainer,
  TeamMemberTeammateAccessGuard,
} from "../components/teamMemberTeammateAccessGuard.js";
import { MyAutomationResultTimeCrumbLabel } from "../features/automations/automationCrumbLabels.js";
import { LinkRedirect } from "../features/links/linkRedirect.js";
import { NavigationSearchGoRoute } from "../features/search/navigationSearchGoRoute.js";
import { createSettingsRoute } from "../features/settingsPage/settingsRoutes.js";
import { TeammateNameCrumbLabel } from "../features/teammatePerformance/performanceCrumbLabels.js";
import { TrainingTeammatePageAutomationResultDetail } from "../features/teammatePerformance/trainingTeammatePageAutomationResultDetail.js";
import { MyAutomationResultRedirectPage } from "../pages/automations/myAutomationResultRedirectPage.js";
import { HomePage } from "../pages/homePage.js";
import { TeamMemberHomePage } from "../pages/teamMemberHomePage.js";
import { TrainingsPage } from "../pages/trainingsPage.js";
import { appPaths } from "./appPaths.js";
import { defineHandle } from "./navigation/appRouteHandle.js";
import { PerformanceNavigationMenu } from "./navigation/menu/performanceNavigationMenu.js";
import { TrainingNavigationMenu } from "./navigation/menu/trainingNavigationMenu.js";
import { WorkspaceId } from "./navigation/workspaces.js";
import { requireRouteParam } from "./routeParams.js";
import { createAgentsWorkspaceRoutes } from "./routes/agentsWorkspaceRoutes.js";
import { createGradingSlideoverRoute } from "./routes/gradingRoutes.js";
import { createPerformanceWorkspaceRoutes } from "./routes/performanceWorkspaceRoutes.js";
import { createPlaybookRoute } from "./routes/playbookRoute.js";
import { createProjectRoute } from "./routes/projectRoutes.js";
import { createTeammatePageRoute } from "./routes/teammatePageRoute.js";

/**
 * The two route trees (regular teammates vs team members), as plain data so
 * tests and tooling can consume them without pulling in the app chrome; the
 * routers themselves are assembled around the shared root layout in
 * routes.tsx. The workspace subtrees are composed from the factories in
 * routes/.
 */

export const appRouteTree: RouteObject[] = [
  {
    path: "/",
    Component: HomePage,
    // Resolve the workspace indicator and menu from the feature-flag-driven
    // default workspace so `/` doesn't flash the recruiting nav for
    // companies without recruiting.
    handle: defineHandle({ defaultWorkspace: true }),
  },
  {
    path: appPaths.go.pattern,
    // Opens the fuzzy search dialog and navigates to the top result.
    Component: NavigationSearchGoRoute,
  },
  {
    path: appPaths.trainings.pattern,
    Component: TrainingsPage,
    handle: defineHandle({
      workspaceId: WorkspaceId.Training,
      navigationMenu: <TrainingNavigationMenu />,
      crumbs: () => [{ label: "Trainings" }],
    }),
  },
  ...createAgentsWorkspaceRoutes(),
  ...createPerformanceWorkspaceRoutes(),
  createProjectRoute(),
  createSettingsRoute(),
  {
    path: "/link/*",
    Component: LinkRedirect,
  },
  {
    // Redirect home on other routes
    path: "*",
    element: <Navigate to="/" />,
  },
];

export const teamMemberAppRouteTree: RouteObject[] = [
  {
    path: "/",
    Component: TeamMemberHomePage,
    handle: defineHandle({
      workspaceId: WorkspaceId.Performance,
      navigationMenu: <PerformanceNavigationMenu />,
    }),
  },
  {
    path: appPaths.go.pattern,
    // Opens the fuzzy search dialog and navigates to the top result.
    Component: NavigationSearchGoRoute,
  },
  createPlaybookRoute([
    createTeammatePageRoute({
      // Team members can't reach the teammates list, so their own page's
      // trail starts at the teammate.
      crumbs: ({ params, pathname }) => [
        {
          label: <TeammateNameCrumbLabel teammateId={params.teammateId} />,
          href: pathname,
        },
      ],
      wrapElement: (element, { withNavigationFallback }) => (
        <TeamMemberTeammateAccessGuard
          fallback={
            withNavigationFallback ? (
              <TeamMemberNavigationSlideoverContainer />
            ) : undefined
          }
        >
          {element}
        </TeamMemberTeammateAccessGuard>
      ),
      includesAutomationResults: true,
    }),
    {
      // The focused single Agent-result view. A standalone route (not a tab of
      // the teammate page) so it renders as its own titled page with a
      // breadcrumb, without the teammate tab header. Reached from the
      // Notifications list and the email/Slack/Teams deep-link redirect.
      path: appPaths.teammateAutomationResult.pattern,
      element: (
        <TeamMemberTeammateAccessGuard
          fallback={<TeamMemberNavigationSlideoverContainer />}
        >
          <TrainingTeammatePageAutomationResultDetail />
        </TeamMemberTeammateAccessGuard>
      ),
      handle: defineHandle({
        workspaceId: WorkspaceId.Performance,
        navigationMenu: <PerformanceNavigationMenu />,
        crumbs: ({ params }) => [
          {
            label: <TeammateNameCrumbLabel teammateId={params.teammateId} />,
            href: appPaths.teammate.build({
              playbookId: requireRouteParam(params, "playbookId"),
              teammateId: requireRouteParam(params, "teammateId"),
            }),
          },
          {
            label: "Notifications",
            href: appPaths.teammateAutomationResults.build({
              playbookId: requireRouteParam(params, "playbookId"),
              teammateId: requireRouteParam(params, "teammateId"),
            }),
          },
          {
            label: (
              <MyAutomationResultTimeCrumbLabel
                playbookId={params.playbookId}
                artifactId={params.artifactId}
              />
            ),
          },
        ],
      }),
    },
    {
      // Playbook-wide grading deep-links (search results, coaching references,
      // share links) point here without a `:teammateId`. Team members can open
      // any teammate's grading detail, but never the all-teammates list: the
      // host renders the grading as a dismissable slideover and redirects to
      // the member's own page when there is no `:gradingId`.
      path: appPaths.playbookCalls.pattern,
      element: <TeamMemberNavigationSlideoverContainer />,
      handle: defineHandle({
        workspaceId: WorkspaceId.Performance,
        navigationMenu: <PerformanceNavigationMenu />,
      }),
      children: [
        createGradingSlideoverRoute({
          path: ":gradingId",
          buildCriterionPath: (params, criterionId) =>
            appPaths.playbookCallCriterion.build({
              playbookId: requireRouteParam(params, "playbookId"),
              gradingId: requireRouteParam(params, "gradingId"),
              criterionId,
            }),
        }),
      ],
    },
  ]),
  {
    // Playbook-free result link a teammate receives by email or Slack/Teams.
    // Resolves the result to this teammate's own home playbook and redirects
    // to their focused Notifications view.
    path: appPaths.automationResult.pattern,
    Component: MyAutomationResultRedirectPage,
  },
  {
    path: appPaths.legacyAutomationResult.pattern,
    Component: MyAutomationResultRedirectPage,
  },
  createSettingsRoute(),
  {
    path: "/link/*",
    Component: LinkRedirect,
  },
  {
    // Redirect home on other routes
    path: "*",
    element: <Navigate to="/" />,
  },
];

interface AdminRouteVariants<T> {
  standard: T;
  teamMember: T;
}

/** Selects the Admin route variant authorized for a resolved teammate. */
export function selectAdminRouteVariant<T>(
  teammate: Teammate,
  variants: AdminRouteVariants<T>,
): T {
  return isTeammateATeamMember(teammate)
    ? variants.teamMember
    : variants.standard;
}

/** Returns the production route tree authorized for a resolved teammate. */
export function getAdminRouteTree(teammate: Teammate): RouteObject[] {
  return selectAdminRouteVariant(teammate, {
    standard: appRouteTree,
    teamMember: teamMemberAppRouteTree,
  });
}
